import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { 
  format,   
  addMonths, 
  startOfMonth, 
  endOfMonth, 
  isValid, 
  parseISO, 
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import toast from 'react-hot-toast';
import { 
  useMediaQuery, 
  useTheme, 
  Box, 
  Typography, 
  CircularProgress, 
  Tooltip,  
  Autocomplete, 
  TextField, 
  ToggleButton,
} from '@mui/material';
import {
  Cached,
  Update,
  Adjust,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import api from 'state/axios';
import { 
  fetchGroupedLegMembersByCrewCode,
  fetchGroupedRosterDetailsByCrewCode,
  fetchCrewBlockHours, 
} from "state/api";
import { 
  parseUTCDate, 
  capitalizeFirstLetter,
  filterCrewMember,
  sortMultipleFields,
} from "state/helpers";
import { LAYOUT_HEIGHT } from 'state/constants';
import { hasPermission } from 'state/checkPermission';
import DataGridComponent from 'components/DataGridComponent';
import ScrollToTop from 'components/ScrollToTop';
import NumberControl from 'components/NumberControl';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import CrewFilterDialog from 'components/CrewFilterDialog';
import RosterDetailsDialog from 'components/RosterDetailsDialog';
import LegMembersDialog from 'components/LegMembersDialog';
import RosterAccumulatesList from './RosterAccumulatesList';


const RosterAccumulates = () => {
  useAuthCheck();
  const user = useSelector((state) => state.user); 
  const params = useSelector((state) => state.params);
  const crewRanksData = useSelector((state) => state.crewRanks);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const canView = hasPermission(user?.permissions, "RosterAccumulates", "view", false);
  const canUpdateAugmented = hasPermission(user?.permissions, "RosterAccumulates", "updateaugmented", false);

  const { startDate, endDate } = useDateRangeContext();

  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState({ isActive: 'ALL' });
  const defaultFilters = {
    isActive: 'ALL',
  };
  const [openLegMembersDialog, setOpenLegMembersDialog] = useState(false);
  const [openRosterDetailsDialog, setOpenRosterDetailsDialog] = useState(false);
  const [selectedCrewCode, setSelectedCrewCode] = useState(null);
  const [selectedCrewName, setSelectedCrewName] = useState(null);
  
  const [isAdjusted, setIsAdjusted] = useState(false);
  const [groupedCrewRosters, setGroupedCrewRosters] = useState([]);
  const [rosterObjects, setRosterObjects] = useState({});
  const [crewStats, setCrewStats] = useState([]);
  const [crewMembers, setCrewMembers] = useState([]);
  const [crewCode, setCrewCode] = useState('');
  const [offSet, setOffSet] = useState(-1);  
  const PROCESSING_TYPE = params?.find((item) => item.param === "PROCESSING_TYPE")?.value || "BATCH"; // or SEQUENCE
    
  
  const fetchCrewMembers = useCallback(async () => {
    return await api.get('/crewmembers');
  }, []);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setError(null);
        setLoading(true);
        
        const [
          crewMembersResponse,
        ] = await Promise.all([
          fetchCrewMembers(),                
        ]);
               
        const sortedCrews = sortMultipleFields(
          crewMembersResponse?.data || [], 
          [ { field: 'crewCode', direction: 'asc' } ], 
          true,
        );

        setCrewMembers(sortedCrews);      
      } catch (error) {
        setError(error);
        toast.error(error.response?.data?.message || "Failed to fetch data!");
      } finally {
        setLoading(false);
      }
    };

    fetchData();      
  }, [fetchCrewMembers]);

  const handleOffSetChange = (newOffSet) => {
    setOffSet(newOffSet); 
  };
  
  const handleCrewBlockHours = useCallback(async () => {
    if (!startDate || !endDate) return null; 
    
    try {
      setError(null);
      setLoading(true);

      const startMonth = startOfMonth(startDate);
      const prev = Math.min(offSet, 0);
      const next = Math.max(offSet, 0);

      const fromDate = parseUTCDate(addMonths(startMonth, prev));
      const toDate = parseUTCDate(endOfMonth(addMonths(startMonth, next)));

      const { data } = await fetchCrewBlockHours(fromDate, toDate, crewCode);   
      setCrewStats(data);   
    } catch (error) {
      setError(error)
      toast.error(error?.message || "Failed to fetch crew block hours!");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, crewCode, offSet]);
  
  const handleRostersCompare = useCallback(async () => {
    if (!startDate) return [];

    try {           
      setError(null);
      setLoading(true);

      const startMonth = startOfMonth(startDate);
      const begin = Math.min(offSet, 0);
      const end = Math.max(offSet, 0);

      // Array indexes
      const monthIndexes = Array.from({ length: end - begin + 1 }, (_, idx) => begin + idx);
      let rostersCollection = {};

      // Call APIs
      if (PROCESSING_TYPE === "BATCH") {
        const result = await Promise.all(
          monthIndexes.map(async (i) => {
            const fromDate = parseUTCDate(addMonths(startMonth, i));
            const toDate = parseUTCDate(endOfMonth(fromDate));
            const key = format(fromDate, "yyyy.MM");

            try {
              const { data } = await fetchCrewBlockHours(fromDate, toDate);
              return [key, data || []];
            } catch (err) {
              toast.error(err?.message || `Fetch error for month ${key}`);
              return [key, []];
            }
          })
        );     
        rostersCollection = Object.fromEntries(result);
      } else if (PROCESSING_TYPE === "SEQUENCE") {
        for (let i = begin; i <= end; i++) {
          let fromDate = parseUTCDate(addMonths(startOfMonth(startDate), i));
          let toDate = parseUTCDate(endOfMonth(fromDate));
          
          const { data } = await fetchCrewBlockHours(fromDate, toDate);
          if (data) {
            let key = `${format(fromDate, 'yyyy.MM')}`;          
            rostersCollection[key] = data;  
          }     
        }  
      }

      setRosterObjects(rostersCollection);
    } catch (error) {
      setError(error);
      toast.error(error?.message || "Failed to fetch accumulated rosters!");
    } finally {
      setLoading(false);
    }
  }, [
    startDate, 
    offSet, 
    PROCESSING_TYPE
  ]); 

  const filteredCrewRosters = useMemo(() => {
    if (!rosterObjects || !filters) return rosterObjects;

    const hasValidFilter = Object.entries(filters).some(([key, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== null && value !== undefined && value !== '';
    });

    if (!hasValidFilter) return rosterObjects;

    return Object.fromEntries(
      Object.entries(rosterObjects).map(([month, crewList]) => {
        const filtered = crewList.filter(member => filterCrewMember(member, filters));
        return [month, filtered];
      })
    );
  }, [rosterObjects, filters]);

  const generatePivotRows = useCallback(() => {
    const allMonths = Object.keys(filteredCrewRosters).sort(); // e.g., ['2025.01', '2025.02', ...]

    const crewMap = new Map();

    for (const month of allMonths) {
      const rosters = filteredCrewRosters[month];

      for (const crew of rosters) {
        const key = crew.crewCode;
        if (!crewMap.has(key)) {
          crewMap.set(key, {
            _id: crew.crewCode,
            crewCode: crew.crewCode,
            crewName: crew.crewName,
            gender: crew.gender,
            post: crew.post,
            base: crew.base,
            trainingQuals: crew.trainingQuals,
            quals: crew.quals,
            dataByMonth: {},
            totalHours: 0,
            monthCount: 0,
            avgHours: 0,
          });
        }
        
        const crewEntry = crewMap.get(key);
        const bh = parseFloat(isAdjusted ? crew.adjustedTotalHours : crew.totalHours) || 0;
        crewEntry.dataByMonth[month] = bh;
        crewEntry.totalHours += bh;
        if (bh > 0) {
          crewEntry.monthCount += 1; 
        }
      }
    }

    // Transform array to render
    return Array.from(crewMap.values()).map(row => {
      const dynamicMonthValues = {};
      allMonths.forEach(month => {
        dynamicMonthValues[month] = row.dataByMonth[month] || 0;
      });

      return {
        ...row,
        ...dynamicMonthValues,
        avgHours: row.monthCount
          ? Number((row.totalHours / row.monthCount).toFixed(2))
          : 0,
      };
    });
  }, [filteredCrewRosters, isAdjusted]);

  const handleFilterOpen = () => setIsFilterDialogOpen(true);
  const handleFilterClose = () => setIsFilterDialogOpen(false);
  
  const handleFilterApply = (filterValues) => {
    setFilters(filterValues); 
  };
  
  const handleViewCrewRosters = useCallback(async (param) => {
    if (!startDate || !endDate) return [];

    const crewCode = param?.crewCode || '';
    const crewName = param?.crewName || '';
    setSelectedCrewCode(crewCode);
    setSelectedCrewName(crewName);
    let result = [];

    const fromDate = offSet < 0 ? addMonths(startDate, offSet) : startDate;
    const toDate = offSet < 0 ? endDate : addMonths(endDate, offSet);

    try {
      result = await fetchGroupedRosterDetailsByCrewCode(crewCode, fromDate, toDate);       
      setGroupedCrewRosters(result);
      setOpenRosterDetailsDialog(true);
      return;
    } catch {      
      setOpenRosterDetailsDialog(false);      
    }

    try {
      result = await fetchGroupedLegMembersByCrewCode(crewCode, fromDate, toDate);
      setGroupedCrewRosters(result);
      setOpenLegMembersDialog(true);
      return;
    } catch (error) {      
      setOpenLegMembersDialog(false);
    } 
  }, [startDate, endDate, offSet]);

  // const handleViewAircraftRoutes = useCallback(async (param) => {
  //   if (!startDate || !endDate) return [];

  //   try {
  //     const crewCode = param.crewCode || '';
  //     const crewName = param.crewName || '';
  //     setSelectedCrewCode(crewCode);
  //     setSelectedCrewName(crewName);
  
  //     const startMonth = startOfMonth(startDate);
  //     const prev = Math.min(offSet, 0);
  //     const next = Math.max(offSet, 0);

  //     const fromDate = parseUTCDate(addMonths(startMonth, prev));
  //     const toDate = parseUTCDate(endOfMonth(addMonths(startMonth, next)));
      
  //     const url = '/aircraftroutes';
  //     const payload = { 
  //       startDate: addDays(fromDate, -1), 
  //       endDate: addDays(toDate, 1),
  //       crewCode, 
  //     };
      
  //     const { data } = await api.post(url, payload);
  //     const { aircraftRoutes } = data;

  //     const filtered = aircraftRoutes?.filter(route => {
  //       const routeDateToCompare = timeMode === 'UTC' 
  //         ? route.routeDate 
  //         : route.routeDateLocalBase;
        
  //       return (
  //         compareAsc(routeDateToCompare, fromDate) >= 0 &&
  //         compareAsc(routeDateToCompare, toDate) <= 0
  //       );
  //     });    
  
  //     const result = filtered?.reduce((acc, cur) => {
  //       const key = `${cur.routeDate.split('T')[0]}|${cur.routeNo}`;

  //       const sectorsWithCrew = cur.sectors?.filter(sector =>
  //         sector.crewMembers?.some(cm => cm.crewCode === crewCode)
  //       );

  //       if (sectorsWithCrew?.length) {
  //         acc[key] = sectorsWithCrew;
  //       }

  //       return acc;
  //     }, {});
  //     setGroupedCrewRosters(result);
  //     setOpenLegMembersDialog(true);
  //     return;
  //   } catch (error) {      
  //     setOpenLegMembersDialog(false);
  //   }       
  // }, [timeMode, startDate, endDate, offSet]);

  const updateAugmentedComposition = useCallback(async () => {
    if (!startDate || !endDate) return [];
    
    try {
      setError(null);
      setLoading(true);

      const url = '/legmembers/updateaugmentedcomposition';
      const payload = { startDate, endDate, offSet};
      await api.put(url, payload);
      
      toast.success("Augmented composition updated successfully.");
    } catch (error) {      
      setError(error);
      toast.error(error?.message || "Failed to update augmented composition!");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, offSet]);

  const handleCloseRosterDetailsDialog = () => {
    setGroupedCrewRosters([]);
    setOpenRosterDetailsDialog(false);
  };

  const handleCloseLegMembersDialog = () => {
    setGroupedCrewRosters([]);
    setOpenLegMembersDialog(false);
  };

  const formatDate = (date) => {
    if (!date) return null;
    return formatInTimeZone(new Date(date), "UTC", "dd/MM/yyyy");
  }
  
  const formatGeneralValue = useCallback((value, indent = 0) => {
    const spacing = " ".repeat(indent * 2);
    if (value == null) return "";

    // Ngày        
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}(T.*)?$/.test(value)) {
      const date = parseISO(value);
      if (isValid(date)) {
        if (value.endsWith("T00:00:00.000Z")) {
          return formatDate(date);
        }

        const hours = date.getUTCHours();
        const minutes = date.getUTCMinutes();
        const seconds = date.getUTCSeconds();
        if (hours === 0 && minutes === 0 && seconds === 0) {
          return formatDate(date);
        }
        return formatInTimeZone(date, "UTC", "HH:mm dd/MM/yyyy");
      }
    }

    // Ngày, Số, Boolean, Chuỗi
    if (value instanceof Date) return formatInTimeZone(value, "UTC", "dd/MM/yyyy");
    if (typeof value === "number") return value.toLocaleString("vi-VN");
    if (typeof value === "boolean") return value ? "✔️" : "❌";
    if (typeof value === "string") return value;

    // Mảng
    if (Array.isArray(value)) {
      if (value.length === 0) return "[]";
      if (value.every(v => typeof v === "string")) return value.join(", ");

      // Mảng object
      return (
        <Typography
          variant="body2"
          component="div"
          sx={{ whiteSpace: "pre-line", fontFamily: "monospace" }}
        >
          {value.map((item, idx) => {
              if (typeof item === "object" && item !== null) {
                return (
                  `${spacing}- ` +
                  Object.entries(item)
                    .map(([k, v]) => `${k}: ${formatGeneralValue(v, indent + 1)}`)
                    .join(", ")
                );
              };
              return `${spacing}- ${formatGeneralValue(item, indent + 1)}`;
            }).join("\n")
          }
        </Typography>
      );
    }

    // Object
    if (typeof value === "object") {
      const entries = Object.entries(value);
      if (entries.length === 0) return "{}";

      return (
        <Typography
          variant="body2"
          component="div"
          sx={{ whiteSpace: "pre-line", fontFamily: "monospace" }}
        >
          {entries
            .map(([k, v]) => `${spacing}${k}: ${formatGeneralValue(v, indent + 1)}`)
            .join("\n")}
        </Typography>
      );
    }

    return String(value);
  }, []);

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,      
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...(() => {
        return isMobile ? { width: 60 } : { flex: 1 };
      })(),
    },    
    {
      field: "crewCode",
      headerName: "Code",
      ...(isMobile ? { width: 80 } : { flex: 1 }),
      renderCell: (params) => {
        return (
          <Box
            // sx={{              
            //   cursor: "pointer",
            //   '&:hover': {
            //     color: "text.primary",
            //     textDecoration: 'underline',
            //   },
            // }}
            // onClick={() => {
            //   if (!canLook) return;
            //   handleViewAircraftRoutes(params.row);
            // }}
          >
            {params.value}
          </Box>
        );
      },
    },    
    {
      field: "crewName",
      headerName: "CrewName",
      cellClassName: "name-column--cell",
      ...(isMobile ? { width: 280 } : { flex: 4 }),
      renderCell: (params) => {
        const gender = params.row.gender; 
        return (
          <Box
            sx={{
              color: gender === 'FM' 
                ? theme.palette.secondary.main 
                : theme.palette.primary.dark,
              cursor: "pointer",
              '&:hover': {
                color: "text.primary",
                textDecoration: 'underline',
              },
            }}
            onClick={() => {
              if (!canView) return;
              handleViewCrewRosters(params.row);
            }}
          >
            {params.value}
          </Box>
        );
      },
    },
    {
      field: "post",
      headerName: "Post",
      ...(isMobile ? { width: 60 } : { flex: 1 }),
    },
    {
      field: "base",
      headerName: "Base",
      ...(isMobile ? { width: 60 } : { flex: 1 }),
    },    
    {
      field: "trainingQuals",
      headerName: "Trainer",
      ...(isMobile ? { width: 60 } : { flex: 1 }),
    },
    {
      field: "quals",
      headerName: "Quals",
      ...(isMobile ? { width: 120 } : { flex: 2 }),
    },
  ], [
    canView,
    // canLook,
    theme, 
    isMobile, 
    handleViewCrewRosters, 
    // handleViewAircraftRoutes
  ]);
  
  const monthColumns = useMemo(() => {
    return Object.keys(filteredCrewRosters).sort().map(month => ({
      field: month,
      headerName: month,
      cellClassName: 'right-align-cell',
      valueFormatter: (params) => {
        const val = parseFloat(params || 0);
        return val.toFixed(2);
      },
      ...(isMobile ? { width: 80 } : { flex: 1.2 }),
    }));
  }, [filteredCrewRosters, isMobile]);

  const totalColumn = {
    field: "totalHours",
    headerName: "TotalHours",
    cellClassName: 'right-align-cell',
    valueFormatter: (params) => parseFloat(params || 0).toFixed(2),
    ...(isMobile ? { width: 100 } : { flex: 1.2 }),
  };

  const avgColumn = {
    field: "avgHours",
    headerName: "AvgHours",
    cellClassName: 'right-align-cell',
    valueFormatter: (params) => parseFloat(params || 0).toFixed(2),
    ...(isMobile ? { width: 100 } : { flex: 1.2 }),
  };

  const hiddenColumns = {  
    _id: false,    
    post: false,
    base: false,
    trainingQuals: false,
    quals: false,
    isActive: false,
  };
  
  const nonMobileHiddenColumns = {
    _id: false,
    trainingQuals: false,
    quals: false,
    isActive: false,
  };

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(!isDesktop ? hiddenColumns : nonMobileHiddenColumns);

  const crewStatsColumns = useMemo(() => {
    const allKeys = new Set();
    crewStats.forEach(row => {
      Object.keys(row).forEach(k => allKeys.add(k));
    });

    return [
      {
          field: "no",
          headerName: "No",
          filterable: false,
          sortable: false,
          width: 60,
          renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      },
      ...Array.from(allKeys).map((key) => {
        const maxCellLength = Math.max(
          key.length,
          ...crewStats?.map(row =>
            row[key] ? String(row[key]).length : 0
          )
        );

        const specialWidths = {
          crewName: 250,
        };
        const columnWidth = specialWidths[key] ?? Math.min(Math.max(maxCellLength * 8, 90), 200);

        const isNumber = crewStats.every(
          (row) =>
            row[key] == null ||
            typeof row[key] === "number" ||
            (!isNaN(row[key]) && row[key] !== "")
        );
        
        // if (key === "crewCode") {
        //   return {
        //     field: key,
        //     headerName: key,
        //     width: columnWidth,
        //     align: "left",
        //     renderCell: (params) => (
        //       <Box
        //         sx={{
        //           cursor: "pointer",
        //           '&:hover': {
        //             color: "text.primary",
        //             textDecoration: 'underline',
        //           },
        //         }}
        //         onClick={() => {
        //           if (!canLook) return;
        //           handleViewAircraftRoutes(params.row);
        //         }}
        //       >
        //         {formatGeneralValue(params.value)}
        //       </Box>
        //     ),
        //   };
        // }
        if (key === "crewName") {
          return {
            field: key,
            headerName: capitalizeFirstLetter(key),
            width: columnWidth,
            align: "left",
            renderCell: (params) => (
              <Box
                sx={{
                  cursor: "pointer",
                  '&:hover': {
                    color: "text.primary",
                    textDecoration: 'underline',
                  },
                }}                
                onClick={() => {
                  if (!canView) return;
                  handleViewCrewRosters(params.row);
                }}
              >
                {formatGeneralValue(params.value)}
              </Box>
            ),
          };
        }
        
        return {
          field: key,
          headerName: capitalizeFirstLetter(key),
          editable: true,
          width: columnWidth,
          align: isNumber ? "right" : "left",
          renderCell: (params) => {
            return (
              <Box
                sx={{
                  height: '100%',
                  alignItems: 'flex-start',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                }}
              >
                {formatGeneralValue(params.value)}
              </Box>
            )
          }                    
        };
      }),
    ];
  }, [
    canView,
    // canLook,
    crewStats, 
    formatGeneralValue, 
    handleViewCrewRosters,
    // handleViewAircraftRoutes,
  ]);

  
  const allRosters = useMemo(() => (
    Object.values(rosterObjects).flat() || []
  ), [rosterObjects]);

  const {
    baseOptions,
    postOptions,
    trainingQualOptions,
    isActiveOptions,
  } = useMemo(() => {
    if (!Array.isArray(allRosters) || allRosters.length === 0) {
      return {
        baseOptions: [],
        postOptions: [],
        trainingQualOptions: [],
        isActiveOptions: [],
      };
    }

    const baseSet = new Set();
    const postSet = new Set();
    const trainingQualSet = new Set();
    const isActiveSet = new Set();

    allRosters.forEach(r => {
      if (r.base) baseSet.add(r.base);
      if (r.post) postSet.add(r.post);
      if (r.isActive !== undefined && r.isActive !== null) {
        isActiveSet.add(r.isActive);
      }

      r.trainingQuals?.forEach(q => q && trainingQualSet.add(q));
    });

    /* base */
    const baseOptions = [...baseSet].sort();

    /* post */
    const rankMap = new Map(
      (crewRanksData ?? []).map(r => [r.rankCode, r])
    );

    const postOptions = [...postSet]
      .map(post => rankMap.get(post))
      .filter(Boolean)
      .sort((a, b) => a.rankPriority - b.rankPriority)
      .map(r => r.rankCode);

    /* training qualification */
    const trainingQualOptions = [...trainingQualSet].sort();

    /* isActive */
    const isActiveOptions = [...isActiveSet].sort();

    return {
      baseOptions,
      postOptions,
      trainingQualOptions,
      isActiveOptions,
    };
  }, [allRosters, crewRanksData]);
  
  const getMonthLabel = (q) => 
    q < -1 ? "Previous months" :
    q === -1 ? "Previous month" :
    q === 0 ? "Current month" :
    q === 1 ? "Next month" :
    q > 1 ? "Next months" : "";

  const label = getMonthLabel(offSet);

  const columnsToShow = crewCode
    ? [...crewStatsColumns]
    : [...columns, ...monthColumns, totalColumn, avgColumn];
    
  const data = useMemo(
    () => (crewCode ? (crewStats || []) : (generatePivotRows() || [])),
    [crewCode, crewStats, generatePivotRows]
  );

  const months = useMemo(() => Object.keys(filteredCrewRosters).sort(), [filteredCrewRosters]);

  const sortedMobileRows = useMemo(() => {
    if (crewCode) return data;
    return [...data].sort((a, b) => (b.totalHours || 0) - (a.totalHours || 0));
  }, [data, crewCode]);

  
  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Roster Accumulates" 
          subtitle={`${loading ? 'Loading...' : 'List of Figures'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {isDesktop && (
              <Autocomplete
                size='small'
                sx={{ minWidth: isMobile ? 80 : 300 }}
                options={crewMembers}
                getOptionLabel={(opt) => `${opt.crewCode} - ${opt.crewName}`}
                value={
                  crewMembers.find((cm) => cm.crewCode === crewCode) || null
                }
                onChange={(_, newValue) => {
                  if (newValue) {
                    setCrewCode(newValue.crewCode);
                  } else {
                    setCrewCode('');
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Crew Member"
                    variant="outlined"
                    required
                  />
                )}
              />
            )}
          
            <Tooltip title={label} arrow>
              <span>
                <NumberControl
                  min={crewCode ? -120 : -23}
                  max={23}
                  value={offSet}
                  onChange={handleOffSetChange} 
                  textFieldWidth={45}
                />
              </span>
            </Tooltip>
            
            {!isMobile && canUpdateAugmented && (
              <Tooltip title={isAdjusted ? "Cumulative BH" : "Full BH"} arrow>
                <ToggleButton        
                  size="small"               
                  value="isAdjusted"
                  selected={isAdjusted}
                  onChange={() => { setIsAdjusted(prev => !prev) }}
                >
                  <Adjust />
                </ToggleButton>   
              </Tooltip>
            )}
                            
            <Tooltip title="Fetch crew rosters" arrow>
              <span>
                <StyledButton 
                  onClick={() => {
                    if (!crewCode) {
                      handleRostersCompare();
                    } else {
                      handleCrewBlockHours();
                    }
                  }}
                >
                  <Cached />
                </StyledButton>
              </span>
            </Tooltip>  

            {!isMobile && canUpdateAugmented && (
              <Tooltip title="Update augmented composition" arrow>
                <span>
                  <StyledButton 
                    onClick={() => {
                      updateAugmentedComposition();
                    }}
                  >
                    <Update />
                  </StyledButton>
                </span>
              </Tooltip>   
            )}         

            <FilterButton
              filters={filters}
              defaultFilters={defaultFilters}
              onClick={handleFilterOpen}
            />
          </Box>
        )}                    
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
        >
          <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
          >
            {isMobile ? (
              <RosterAccumulatesList
                rows={sortedMobileRows}
                isStats={!!crewCode}
                months={months}
                canView={canView}
                onViewClick={handleViewCrewRosters}
              />
            ) : (
              <DataGridComponent
                rows={data}
                columns={columnsToShow}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                props={{
                  initialState: {
                    pagination: {
                      paginationModel: { pageSize: 100 },
                    },
                  },
                }}
              />
            )}

            <ScrollToTop scrollRef={scrollRef} />
          </Box>
        </Box>
      )}

      {isFilterDialogOpen &&
        <CrewFilterDialog
          open={isFilterDialogOpen}
          onClose={handleFilterClose}
          onApply={handleFilterApply}
          postOptions={postOptions}
          baseOptions={baseOptions}
          trainingQualOptions={trainingQualOptions}
          isActiveOptions={isActiveOptions}
          currentFilters={filters}
        />
      }
      
      {openRosterDetailsDialog &&
        <RosterDetailsDialog 
          open={openRosterDetailsDialog}
          onClose={handleCloseRosterDetailsDialog}
          crewCode={selectedCrewCode}
          crewName={selectedCrewName}
          crewRosters={groupedCrewRosters}
        />
      }

      {openLegMembersDialog &&
        <LegMembersDialog 
          open={openLegMembersDialog}
          onClose={handleCloseLegMembersDialog}
          crewCode={selectedCrewCode}
          crewName={selectedCrewName}
          crewLegs={groupedCrewRosters}
        />
      }
    </Box>
  );
};

export default RosterAccumulates;
